import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import api from '../config/axios';
import ChallengeQr from '../components/ChallengeQr';
import QrPosterPanel from './QrPosterPanel';
import PresenceWindowSettings from './PresenceWindowSettings';
import PageHeader from '../components/ui/PageHeader';
import SectionCard from '../components/ui/SectionCard';
import {
  challengeClockOffset,
  challengeCountdown,
  formatChallengeExpiry,
  formatCountdown,
} from '../utils/challengeDisplay';
import { formatLocalTimestamp } from '../utils/hackathonFlow';
import {
  MAIN_EVENT,
  activityLabel,
  filterAttempts,
  filterPresenceRecords,
  methodLabel,
  operationLabel,
  presenceSummary,
  reasonLabel,
  resultLabel,
} from '../utils/presenceAdmin';

const REFRESH_MS = 20000;
const statusTone = { complete: 'success', check_in_only: 'warning', check_out_only: 'error' };

const formatPresenceDay = (value) => {
  const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return matched ? `${matched[3]}/${matched[2]}/${matched[1]}` : '—';
};

function ActivityFilter({ value, onChange, activities }) {
  return (
    <TextField select size="small" label="Atividade" value={value} onChange={(event) => onChange(event.target.value)} sx={{ minWidth: 200 }}>
      <MenuItem value="">Todas</MenuItem>
      <MenuItem value={MAIN_EVENT}>Evento principal</MenuItem>
      {activities.map((activity) => <MenuItem key={activity.id} value={String(activity.id)}>{activity.nome}</MenuItem>)}
    </TextField>
  );
}

export default function PresenceAdminPage() {
  const [params] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(params.get('evento') || '');
  const [operation, setOperation] = useState('check_in');
  const [subEventId, setSubEventId] = useState('');
  const [activities, setActivities] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [challengeOffset, setChallengeOffset] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [fixedLink, setFixedLink] = useState(null);
  const [postersOpen, setPostersOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [recordFilter, setRecordFilter] = useState({ activity: '', status: '', query: '' });
  const [attemptFilter, setAttemptFilter] = useState({ activity: '', result: '', query: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/events').then((response) => setEvents(response.data || [])).catch(() => setError('Não foi possível carregar os eventos.'));
  }, []);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}/sub-events`)
      .then((response) => setActivities(response.data || []))
      .catch(() => setActivities([]));
  }, [eventId]);

  useEffect(() => {
    if (!challenge) return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);

  const generate = async () => {
    setError('');
    setChallenge(null);
    try {
      const response = await api.post(`/hackathon/admin/events/${eventId}/challenges`, {
        operation,
        subEventId: subEventId || null,
      });
      const receivedAt = Date.now();
      setChallengeOffset(challengeClockOffset(response.data.challenge?.serverNow, receivedAt));
      setNowMs(receivedAt);
      setChallenge(response.data.challenge);
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível gerar o desafio.');
    }
  };

  const generateFixed = async () => {
    setError('');
    setFixedLink(null);
    try {
      const response = await api.post(`/hackathon/admin/events/${eventId}/checkin-links`, {
        subEventId: subEventId || null,
      });
      setFixedLink(response.data.link);
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível criar o QR fixo.');
    }
  };

  const revokeFixed = async () => {
    if (!fixedLink?.id) return;
    setError('');
    try {
      await api.post(`/hackathon/admin/checkin-links/${fixedLink.id}/revoke`);
      setFixedLink(null);
    } catch (reason) {
      setError(reason?.response?.data?.message || 'Não foi possível revogar o QR fixo.');
    }
  };

  const loadLists = useCallback(async () => {
    if (!eventId) return;
    try {
      const [presenceResponse, attemptResponse] = await Promise.all([
        api.get(`/hackathon/admin/events/${eventId}/presence`),
        api.get(`/hackathon/admin/events/${eventId}/attempts`),
      ]);
      setRecords(presenceResponse.data.records || []);
      setAttempts(attemptResponse.data.attempts || []);
      setUpdatedAt(new Date());
    } catch (reason) {
      setRecords([]);
      setAttempts([]);
      setError(reason?.response?.data?.message || 'Não foi possível carregar as listas do evento.');
    }
  }, [eventId]);

  useEffect(() => {
    loadLists();
    if (!eventId) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') loadLists();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [eventId, loadLists]);

  const exportCsv = async () => {
    const response = await api.get(`/hackathon/admin/events/${eventId}/attendance.csv`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `presenca-evento-${eventId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const countdown = challenge ? challengeCountdown(challenge.expiresAt, nowMs, challengeOffset) : null;
  const challengeActivity = challenge
    ? activityLabel(activities.find((item) => String(item.id) === String(challenge.subEventId))?.nome)
    : '';
  const visibleRecords = filterPresenceRecords(records, recordFilter);
  const summary = presenceSummary(visibleRecords);
  const visibleAttempts = filterAttempts(attempts, attemptFilter);
  const acceptedCount = visibleAttempts.filter((row) => row.result === 'accepted').length;

  return (
    <Stack spacing={2}>
      <PageHeader title="Presença do evento" subtitle="Gere um QR ou PIN curto. Ele não guarda senha nem dado pessoal." />
      {error ? <Alert severity="error">{error}</Alert> : null}
      <SectionCard>
        <Stack spacing={2}>
          <TextField select label="Evento" value={events.some((event) => String(event.id) === eventId) ? eventId : ''} onChange={(event) => { setEventId(String(event.target.value)); setFixedLink(null); setChallenge(null); setPostersOpen(false); }}>
            {events.map((event) => <MenuItem key={event.id} value={String(event.id)}>{event.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Atividade" value={subEventId} onChange={(event) => setSubEventId(event.target.value)}>
            <MenuItem value="">Evento principal</MenuItem>
            {activities.map((activity) => <MenuItem key={activity.id} value={activity.id}>{activity.nome}</MenuItem>)}
          </TextField>
          <TextField select label="Operação" value={operation} onChange={(event) => setOperation(event.target.value)}>
            <MenuItem value="check_in">Entrada</MenuItem>
            <MenuItem value="check_out">Saída</MenuItem>
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" disabled={!eventId} onClick={generate}>Gerar QR/PIN</Button>
            <Button variant="outlined" disabled={!eventId} onClick={generateFixed}>Gerar QR fixo</Button>
            <Button variant="outlined" disabled={!eventId} onClick={() => setPostersOpen(true)}>Preparar impressão</Button>
            <Button variant="outlined" disabled={!eventId} onClick={loadLists}>Atualizar listas</Button>
            <Button variant="outlined" disabled={!eventId} onClick={exportCsv}>Exportar registros</Button>
          </Stack>
          {fixedLink ? (
            <Stack spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
              <Typography variant="h6">QR fixo para impressão</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Vale até ser revogado. Não registra presença sozinho: o servidor ainda exige inscrição, localização dentro do raio e a janela de entrada ou saída.
              </Typography>
              <ChallengeQr payload={`${window.location.origin}/hackathon?ref=${fixedLink.publicCode}`} />
              <Button variant="text" onClick={revokeFixed}>Revogar QR fixo</Button>
            </Stack>
          ) : null}
          {challenge ? (
            <Stack spacing={1} sx={{ width: '100%', maxWidth: '100%', minWidth: 0, alignItems: 'center' }}>
              <Typography variant="h6" component="p" sx={{ textAlign: 'center' }}>
                {operationLabel[challenge.operation]} · {challengeActivity}
              </Typography>
              {countdown.expired ? (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Este QR/PIN expirou às {formatChallengeExpiry(challenge.expiresAt)}. Gere um novo para continuar.
                </Alert>
              ) : (
                <>
                  <ChallengeQr payload={challenge.qrPayload} />
                  <Typography variant="overline" component="p">PIN</Typography>
                  <Typography variant="h2" component="p" sx={{ letterSpacing: '0.08em' }}>{challenge.pin}</Typography>
                  <Chip
                    color={countdown.remaining <= 30 ? 'warning' : 'success'}
                    label={`Expira em ${formatCountdown(countdown.remaining)}`}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    aria-live="off"
                  />
                  <Typography variant="body2">Válido até {formatChallengeExpiry(challenge.expiresAt)} (horário de Brasília)</Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                O QR e o PIN são o mesmo código: valem {Math.round((challenge.ttlSeconds || 300) / 60)} minutos, só para esta atividade e operação.
                Gerar outro substitui este na hora. Anote o PIN: ele não será mostrado de novo.
              </Typography>
              <Box component="details" sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                <Box component="summary" sx={{ cursor: 'pointer' }}>Detalhe técnico</Box>
                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{challenge.qrPayload}</Typography>
              </Box>
            </Stack>
          ) : null}
        </Stack>
      </SectionCard>

      <PresenceWindowSettings eventId={eventId} activities={activities} />

      {postersOpen && eventId ? (
        <SectionCard>
          <QrPosterPanel eventId={eventId} />
        </SectionCard>
      ) : null}

      <SectionCard>
        <Stack spacing={2}>
          <Typography variant="h3" component="h2">Presenças registradas</Typography>
          <Typography variant="body2" color="text.secondary">
            Uma linha por participante, dia e atividade, montada só com entradas e saídas gravadas pelo servidor.
            Tentativas, aceitas ou recusadas, ficam no histórico abaixo.
            {updatedAt ? ` Atualizado às ${updatedAt.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}; atualiza sozinho a cada 20 s.` : ''}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <ActivityFilter value={recordFilter.activity} activities={activities} onChange={(activity) => setRecordFilter((current) => ({ ...current, activity }))} />
            <TextField select size="small" label="Situação" value={recordFilter.status} onChange={(event) => setRecordFilter((current) => ({ ...current, status: event.target.value }))} sx={{ minWidth: 180 }}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="complete">Presença completa</MenuItem>
              <MenuItem value="check_in_only">Somente entrada</MenuItem>
              <MenuItem value="check_out_only">Somente saída (sem check-in)</MenuItem>
            </TextField>
            <TextField size="small" label="Buscar participante" value={recordFilter.query} onChange={(event) => setRecordFilter((current) => ({ ...current, query: event.target.value }))} />
          </Stack>
          <Typography variant="body2">
            {summary.total} registro(s) · {summary.complete} com presença completa · {summary.checkInOnly} somente com entrada · {summary.checkOutOnly} somente com saída
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Presenças registradas">
              <TableHead>
                <TableRow>
                  <TableCell>Participante</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Atividade</TableCell>
                  <TableCell>Dia</TableCell>
                  <TableCell>Entrada</TableCell>
                  <TableCell>Saída</TableCell>
                  <TableCell>Duração</TableCell>
                  <TableCell>Situação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRecords.map((row) => (
                  <TableRow key={`${row.attendeeId}-${row.subEventId ?? 0}-${row.presenceDay || 'single'}`}>
                    <TableCell>
                      {row.nome}
                      <Typography variant="caption" color="text.secondary" component="p">{row.email}</Typography>
                    </TableCell>
                    <TableCell>{row.eventName}</TableCell>
                    <TableCell>{activityLabel(row.activityName)}</TableCell>
                    <TableCell>{formatPresenceDay(row.presenceDay)}</TableCell>
                    <TableCell>{formatLocalTimestamp(row.checkInAt)}</TableCell>
                    <TableCell>{formatLocalTimestamp(row.checkOutAt)}</TableCell>
                    <TableCell>{row.status === 'complete' && row.presenceSeconds != null ? `${row.presenceSeconds} s` : '—'}</TableCell>
                    <TableCell>
                      {row.status === 'check_out_only'
                        ? <Chip size="small" color="warning" label="Saída sem entrada" />
                        : <Chip size="small" color={statusTone[row.status] || 'default'} label={row.statusLabel} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {visibleRecords.length === 0 ? (
            <Typography color="text.secondary">
              {records.length === 0 ? 'Nenhuma presença gravada neste evento.' : 'Nenhuma presença com estes filtros.'}
            </Typography>
          ) : null}
        </Stack>
      </SectionCard>

      <SectionCard>
        <Stack spacing={1.5}>
          <Typography variant="h3" component="h2">Histórico de tentativas</Typography>
          <Typography variant="body2" color="text.secondary">
            Auditoria das últimas 500 tentativas. Uma tentativa aceita corresponde a uma entrada ou saída gravada; recusadas não contam como presença.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <ActivityFilter value={attemptFilter.activity} activities={activities} onChange={(activity) => setAttemptFilter((current) => ({ ...current, activity }))} />
            <TextField select size="small" label="Resultado" value={attemptFilter.result} onChange={(event) => setAttemptFilter((current) => ({ ...current, result: event.target.value }))} sx={{ minWidth: 160 }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="accepted">Aceitas</MenuItem>
              <MenuItem value="rejected">Recusadas</MenuItem>
            </TextField>
            <TextField size="small" label="Buscar participante" value={attemptFilter.query} onChange={(event) => setAttemptFilter((current) => ({ ...current, query: event.target.value }))} />
          </Stack>
          <Typography variant="body2">
            {visibleAttempts.length} tentativa(s) · {acceptedCount} aceita(s) · {visibleAttempts.length - acceptedCount} recusada(s)
          </Typography>
          {visibleAttempts.length === 0 ? (
            <Typography color="text.secondary">Nenhuma tentativa com estes filtros.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Histórico de tentativas">
                <TableHead>
                  <TableRow>
                    <TableCell>Horário</TableCell>
                    <TableCell>Participante</TableCell>
                    <TableCell>Atividade</TableCell>
                    <TableCell>Operação</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleAttempts.map((row, index) => (
                    <TableRow key={row.id ?? `${row.server_recorded_at}-${index}`}>
                      <TableCell>{formatLocalTimestamp(row.server_recorded_at)}</TableCell>
                      <TableCell>{row.attendee_nome || '—'}</TableCell>
                      <TableCell>{activityLabel(row.activity_nome)}</TableCell>
                      <TableCell title={row.operation ? undefined : 'A operação não fica gravada em tentativas recusadas antes da validação.'}>
                        {operationLabel[row.operation] || '—'}
                      </TableCell>
                      <TableCell>{methodLabel[row.validation_method] || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={row.result === 'accepted' ? 'success' : 'default'} label={resultLabel[row.result] || row.result} />
                      </TableCell>
                      <TableCell>
                        {row.result === 'accepted' && !row.presence_stored
                          ? 'Aceita, mas sem registro gravado: não conta como presença'
                          : reasonLabel[row.reason_code] || row.reason_code || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </SectionCard>
    </Stack>
  );
}
